import { createContext, useContext, useState, useRef, useEffect, useCallback } from 'react';
import { getActiveProviderType } from '../services/endpoint';
import { useAuth } from '../components/Auth/AuthContext';

const CallModeContext = createContext();

export const CallModeProvider = ({ children }) => {
  // CallModeProvider is mounted inside AuthProvider (see index.jsx), so auth state
  // is available here and is the correct trigger for the provider-type fetch.
  const { user, authChecked } = useAuth();
const VALID_CALL_MODES = ['Inbound', 'Outbound', 'Blended'];

const [callMode, setCallModeRaw] = useState(() => {
  const saved = localStorage.getItem('callMode');
  return VALID_CALL_MODES.includes(saved) ? saved : 'Blended';
});

const setCallMode = useCallback((mode) => {
  if (!VALID_CALL_MODES.includes(mode)) return;
  localStorage.setItem('callMode', mode);
  setCallModeRaw(mode);
}, []);

const [callStatus, setCallStatus] = useState('offline');
  const [callNumber, setCallNumber] = useState('');
  const [callDuration, setCallDuration] = useState(0);
  const [callMuted, setCallMuted] = useState(false);
  const [activeCallId, setActiveCallId] = useState(null);
  const [isIncoming, setIsIncoming] = useState(false);
  const activeSessionRef = useRef(null);
  const dialerActionsRef = useRef({});

  // linkedCallId tracks the TeleCMI cmiuuid for the current/last call so that
  // CompaignModule can attach it to the disposition submission even after the
  // call has ended (activeCallId is cleared on hangup).
  const [linkedCallId, setLinkedCallId] = useState('');
  const linkedCallIdRef = useRef('');

  const linkCallId = useCallback((id) => {
    if (!id) return;
    linkedCallIdRef.current = id;
    setLinkedCallId(id);
    setActiveCallId(id);
  }, []);

  const clearLinkedCallId = useCallback(() => {
    linkedCallIdRef.current = '';
    setLinkedCallId('');
  }, []);

  const [providerType, setProviderType] = useState(null);
  const [providerLoading, setProviderLoading] = useState(true);
  // Whether THIS user can actually place a call. providerType only says the
  // ORGANISATION has telephony; credentials are per user, so a supervisor who
  // only runs meetings (or an agent holding credentials for a provider the org
  // no longer uses) has none. Screens that offer a dialler must gate on this,
  // not on providerType, or they hand out a softphone that cannot register.
  const [canCall, setCanCall] = useState(false);
  const [cannotCallReason, setCannotCallReason] = useState('');

  // Transfer state shared from host → embedded panel
  const [transferState, setTransferState] = useState({ state: null, target: '', error: '' });

  // Wait for auth before asking which telephony provider the org uses.
  // This used to be a mount-once effect that read sessionStorage.access_token
  // synchronously. CallModeProvider is a CHILD of AuthProvider, whose initAuth() is
  // async: on a cold load with an expired access token it awaits refreshToken() before
  // writing the new one. So this effect ran first, saw no token, bailed, and left
  // providerType null for the entire session with no retry — DialerProvider then fell
  // back to the legacy dialer and the softphone showed "Offline / N/A" until a manual
  // page refresh. Keying on authChecked/user makes it run once auth is actually settled.
  const fetchProviderType = useCallback(async ({ signal } = {}) => {
    const aborted = () => signal?.aborted;
    setProviderLoading(true);

    // Retry transient failures. A single network blip previously disabled the
    // dialler for the whole session.
    const delays = [0, 1500, 4000];
    for (let attempt = 0; attempt < delays.length; attempt += 1) {
      if (delays[attempt]) {
        await new Promise(r => setTimeout(r, delays[attempt]));
      }
      if (aborted()) return;
      try {
        const res = await getActiveProviderType();
        if (aborted()) return;
        const d = res?.data?.data || {};
        setProviderType(d.provider_type || null);
        // Falls back to "provider exists" when can_call is absent, so a
        // frontend deployed ahead of the backend keeps its old behaviour
        // instead of hiding the dialler from everyone.
        setCanCall(
          typeof d.can_call === 'boolean' ? d.can_call : !!d.provider_type
        );
        setCannotCallReason(d.cannot_call_reason || '');
        setProviderLoading(false);
        return;
      } catch (err) {
        // 401/403 mean this user genuinely has no telephony access — retrying
        // will not change the answer, so stop immediately.
        const code = err?.response?.status;
        if (code === 401 || code === 403) break;
      }
    }

    if (aborted()) return;
    setProviderType(null);
    setCanCall(false);
    setCannotCallReason('');
    setProviderLoading(false);
  }, []);

  useEffect(() => {
    // Auth still resolving — stay in the loading state so DialerProvider shows a
    // skeleton rather than committing to the wrong dialer.
    if (!authChecked) {
      setProviderLoading(true);
      return undefined;
    }

    // Superadmin operates in public schema and has no org — skip provider fetch
    if (!user || user.role === 'superadmin') {
      setProviderType(null);
      setCanCall(false);
      setProviderLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    fetchProviderType({ signal: controller.signal });
    return () => controller.abort();
  }, [authChecked, user?.id, user?.role, fetchProviderType]);

  return (
    <CallModeContext.Provider value={{
      callMode, setCallMode,
      callStatus, setCallStatus,
      callNumber, setCallNumber,
      callDuration, setCallDuration,
      callMuted, setCallMuted,
      activeCallId, setActiveCallId,
      isIncoming, setIsIncoming,
      activeSessionRef,
      dialerActionsRef,
      providerType, setProviderType, providerLoading,
      // Manual retry hook so a UI can recover without a full page reload.
      refreshProviderType: fetchProviderType,
      canCall, cannotCallReason,
      // Transfer state — host pushes updates, embedded panel reads
      transferState, setTransferState,
      // Call ID tracking — used by CompaignModule to attach cmiuuid to disposition
      linkedCallId,
      linkedCallIdRef,
      linkCallId,
      clearLinkedCallId,
    }}>
      {children}
    </CallModeContext.Provider>
  );
};

export const useCallMode = () => useContext(CallModeContext);
